import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { panelApi } from "@/lib/api";
import { cn } from "@/lib/utils";
import { localDate } from "#shared/timeclock";
import type { Employee, VacationKind, VacationStatus } from "#shared/types";
import { VACATION_KINDS } from "#shared/types";
import {
  countWorkdays,
  formatDateRange,
  VACATION_LABELS,
  VACATION_STATUS_LABELS,
} from "#shared/vacation";

const STATUS_STYLES: Record<VacationStatus, string> = {
  vantande: "bg-warning/20 text-warning-foreground",
  godkand: "bg-success/15 text-success",
  nekad: "bg-destructive/12 text-destructive",
  aterkallad: "bg-muted text-muted-foreground",
};

export function VacationRequestDialog({
  employee,
  onClose,
}: {
  employee: Employee | null;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const today = localDate();

  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [kind, setKind] = useState<VacationKind>("semester");
  const [message, setMessage] = useState("");

  const requests = useQuery({
    queryKey: ["vacation", employee?.id],
    queryFn: () => panelApi.vacationFor(employee!.id),
    enabled: !!employee,
  });

  function reset() {
    setStartDate(today);
    setEndDate(today);
    setKind("semester");
    setMessage("");
  }

  const apply = useMutation({
    mutationFn: () =>
      panelApi.applyForVacation({
        employee_id: employee!.id,
        start_date: startDate,
        end_date: endDate,
        kind,
        message: message.trim() || null,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vacation"] });
      toast.success("Ansökan är skickad. En administratör svarar så snart det går.");
      reset();
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const withdraw = useMutation({
    mutationFn: (id: string) => panelApi.withdrawVacation(id, employee!.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["vacation"] });
      toast.success("Ansökan är återkallad.");
    },
    onError: (error: Error) => toast.error(error.message),
  });

  const days = countWorkdays(startDate, endDate);
  const invalidRange = endDate < startDate;

  return (
    <Dialog
      open={!!employee}
      onOpenChange={(open) => {
        if (!open) {
          reset();
          onClose();
        }
      }}
    >
      <DialogContent className="max-h-[92vh] max-w-lg overflow-y-auto rounded-3xl">
        <DialogHeader>
          <DialogTitle>Ansök om ledighet</DialogTitle>
          <DialogDescription>{employee?.name}</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="mb-2 block">Typ av ledighet</Label>
            <div className="grid grid-cols-2 gap-2">
              {VACATION_KINDS.map((option) => (
                <button
                  key={option}
                  onClick={() => setKind(option)}
                  className={cn(
                    "press h-12 rounded-2xl text-sm font-medium",
                    kind === option
                      ? "bg-primary text-primary-foreground"
                      : "bg-secondary text-secondary-foreground",
                  )}
                >
                  {VACATION_LABELS[option]}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="start-date">Första dagen</Label>
              <Input
                id="start-date"
                type="date"
                value={startDate}
                onChange={(event) => {
                  setStartDate(event.target.value);
                  if (event.target.value > endDate) setEndDate(event.target.value);
                }}
                className="h-12"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="end-date">Sista dagen</Label>
              <Input
                id="end-date"
                type="date"
                min={startDate}
                value={endDate}
                onChange={(event) => setEndDate(event.target.value)}
                className="h-12"
              />
            </div>
          </div>

          <p className="rounded-2xl bg-muted px-4 py-3 text-sm">
            {invalidRange ? (
              <span className="text-destructive">Sista dagen kan inte vara före första dagen.</span>
            ) : days === 0 ? (
              <span className="text-destructive">
                Perioden innehåller inga arbetsdagar. Välj andra datum.
              </span>
            ) : (
              <>
                <strong>
                  {days} {days === 1 ? "arbetsdag" : "arbetsdagar"}
                </strong>{" "}
                — helger och röda dagar räknas inte.
              </>
            )}
          </p>

          <div className="space-y-1.5">
            <Label htmlFor="vacation-message">Meddelande (frivilligt)</Label>
            <Textarea
              id="vacation-message"
              value={message}
              onChange={(event) => setMessage(event.target.value)}
              placeholder="T.ex. resa som redan är bokad"
              rows={2}
            />
          </div>

          <Button
            className="press h-14 w-full rounded-2xl text-base"
            disabled={apply.isPending || invalidRange || days === 0}
            onClick={() => apply.mutate()}
          >
            {apply.isPending ? "Skickar…" : "Skicka ansökan"}
          </Button>
        </div>

        <section className="mt-2 border-t pt-4">
          <h3 className="text-sm font-semibold">Dina ansökningar</h3>
          {requests.isLoading ? (
            <p className="py-4 text-sm text-muted-foreground">Hämtar…</p>
          ) : (requests.data ?? []).length === 0 ? (
            <p className="py-4 text-sm text-muted-foreground">
              Du har inga ledighetsansökningar ännu.
            </p>
          ) : (
            <ul className="mt-2 divide-y">
              {requests.data!.map((request) => (
                <li key={request.id} className="flex items-center gap-3 py-2.5">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium">
                      {formatDateRange(request.start_date, request.end_date)}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {VACATION_LABELS[request.kind]} · {request.days}{" "}
                      {request.days === 1 ? "dag" : "dagar"}
                      {request.decision_note ? ` · ${request.decision_note}` : ""}
                    </p>
                  </div>
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-1 text-xs font-semibold whitespace-nowrap",
                      STATUS_STYLES[request.status],
                    )}
                  >
                    {VACATION_STATUS_LABELS[request.status]}
                  </span>
                  {request.status === "vantande" && (
                    <Button
                      variant="ghost"
                      className="h-9 rounded-full text-xs"
                      disabled={withdraw.isPending}
                      onClick={() => withdraw.mutate(request.id)}
                    >
                      Återkalla
                    </Button>
                  )}
                </li>
              ))}
            </ul>
          )}
        </section>
      </DialogContent>
    </Dialog>
  );
}
